import { inject, Injectable } from '@angular/core';
import { AnalyticsService } from '../../../../../../../shared/services/analytics.service';

/**
 * W48: every Drawings PostHog event, by name and with the properties it may carry. Ids, counts, enums, booleans and
 * durations only: never a sheet title or number, notes, file names, search text or a location. The project is already
 * on every event (AnalyticsService.setProjectContext), so it is not repeated here.
 */
export const DRAWINGS_EVENTS = {
  // Upload
  revision_upload_started: { name: 'drawings.revision_upload_started', props: ['sheet_uuid'] },
  set_upload_started: { name: 'drawings.set_upload_started', props: ['size_mb'] },
  set_upload_succeeded: { name: 'drawings.set_upload_succeeded', props: ['set_uuid', 'size_mb', 'duration_ms'] },
  set_upload_failed: { name: 'drawings.set_upload_failed', props: ['error_code', 'status', 'size_mb'] },
  set_inspect_finished: { name: 'drawings.set_inspect_finished', props: ['set_uuid', 'pages', 'outcome', 'needs_attention', 'error_code'] },
  // Set review
  set_review_opened: { name: 'drawings.set_review_opened', props: ['set_uuid', 'pages', 'needs_attention', 'skipped', 'status'] },
  review_filter_changed: { name: 'drawings.review_filter_changed', props: ['set_uuid', 'filter'] },
  review_view_changed: { name: 'drawings.review_view_changed', props: ['set_uuid', 'view'] },
  review_banner_clicked: { name: 'drawings.review_banner_clicked', props: ['set_uuid', 'needs_attention', 'source'] },
  page_panel_opened: { name: 'drawings.page_panel_opened', props: ['set_uuid', 'source'] },
  page_field_saved: { name: 'drawings.page_field_saved', props: ['set_uuid', 'field', 'source', 'fixed_problem'] },
  page_field_save_failed: { name: 'drawings.page_field_save_failed', props: ['set_uuid', 'field', 'source', 'error_code', 'status'] },
  issue_navigated: { name: 'drawings.issue_navigated', props: ['set_uuid', 'direction', 'filtered', 'remaining'] },
  page_skip_toggled: { name: 'drawings.page_skip_toggled', props: ['set_uuid', 'skip', 'source'] },
  page_dropped: { name: 'drawings.page_dropped', props: ['set_uuid'] },
  bulk_applied: { name: 'drawings.bulk_applied', props: ['set_uuid', 'selected', 'fields', 'succeeded', 'failed'] },
  bulk_retried: { name: 'drawings.bulk_retried', props: ['set_uuid', 'count'] },
  bulk_selection_changed: { name: 'drawings.bulk_selection_changed', props: ['set_uuid', 'method', 'selected', 'view'] },
  page_viewer_opened: { name: 'drawings.page_viewer_opened', props: ['set_uuid', 'mode'] },
  page_viewer_sharp_render: { name: 'drawings.page_viewer_sharp_render', props: ['ok', 'ms', 'zoom'] },
  publish_dialog_opened: { name: 'drawings.publish_dialog_opened', props: ['set_uuid', 'sheets', 'skipped', 'replaces'] },
  set_discarded: { name: 'drawings.set_discarded', props: ['set_uuid', 'pages', 'source'] },
  set_published: { name: 'drawings.set_published', props: ['set_uuid', 'sheets', 'skipped', 'replaces', 'has_notes', 'review_duration_s'] },
  set_publish_failed: { name: 'drawings.set_publish_failed', props: ['set_uuid', 'error_code', 'status'] },
  /** Wrong-drawing guard: the publish's 409 listed pages to acknowledge */
  publish_warnings_shown: { name: 'drawings.publish_warnings_shown', props: ['set_uuid', 'pages'] },
  // Register, viewer and the rest
  add_record_started: { name: 'drawings.add_record_started', props: [] },
  add_record_placed: { name: 'drawings.add_record_placed', props: ['type'] },
  compare_mode_changed: { name: 'drawings.compare_mode_changed', props: ['mode'] },
  compare_aligned: { name: 'drawings.compare_aligned', props: [] },
  compare_overlay_toggled: { name: 'drawings.compare_overlay_toggled', props: ['removed', 'added'] },
  sheet_zoomed: { name: 'drawings.sheet_zoomed', props: ['method'] },
  sheet_fullscreen_toggled: { name: 'drawings.sheet_fullscreen_toggled', props: ['on', 'source'] },
  viewer_filter_changed: { name: 'drawings.viewer_filter_changed', props: ['sections', 'count'] },
  register_filter_changed: { name: 'drawings.register_filter_changed', props: ['sections', 'count'] },
  register_group_changed: { name: 'drawings.register_group_changed', props: ['group'] },
  register_searched: { name: 'drawings.register_searched', props: ['has_station_query', 'query_length', 'results', 'discipline_filter', 'type_filter'] },
  at_my_station_used: { name: 'drawings.at_my_station_used', props: ['outcome', 'results'] },
  sheet_details_saved: { name: 'drawings.sheet_details_saved', props: ['fields'] },
  sheet_opened: { name: 'drawings.sheet_opened', props: ['sheet_uuid', 'source'] },
  sheet_text_find_used: { name: 'drawings.sheet_text_find_used', props: ['matches'] },
  revision_compare_opened: { name: 'drawings.revision_compare_opened', props: ['sheet_uuid'] },
  markup_saved: { name: 'drawings.markup_saved', props: ['sheet_uuid', 'shared', 'shapes'] },
  sheet_downloaded: { name: 'drawings.sheet_downloaded', props: ['mode'] },
  set_exported: { name: 'drawings.set_exported', props: ['kind', 'outcome'] },
  placement_saved: { name: 'drawings.placement_saved', props: ['revision_uuid', 'method', 'rms_ft', 'check_point'] },
  placement_failed: { name: 'drawings.placement_failed', props: ['method', 'error_code', 'status'] },
  as_built_saved: { name: 'drawings.as_built_saved', props: ['sheet_uuid'] },
  as_built_reviewed: { name: 'drawings.as_built_reviewed', props: ['status', 'count', 'source'] },
  record_linked: { name: 'drawings.record_linked', props: ['record_type'] },
  carry_forward_used: { name: 'drawings.carry_forward_used', props: ['set_uuid', 'count'] },
  // Records: the location picker's link (W51) and its "Mark on drawing" entry (W49)
  record_location_set: { name: 'drawings.record_location_set', props: ['target', 'has_markup'] },
  mark_on_drawing_clicked: { name: 'drawings.mark_on_drawing_clicked', props: ['record_type'] },
  // Any other error a drawings screen shows
  error_shown: { name: 'drawings.error_shown', props: ['surface', 'error_code', 'status'] },
} as const;

export type DrawingsEvent = keyof typeof DRAWINGS_EVENTS;
type PropValue = string | number | boolean | null | readonly string[];

/** `error_code` (the API's code, or NETWORK when nothing answered) and `status`; never the message */
export function errorProps(e: unknown): { error_code: string; status: number | null } {
  const err = e as { status?: unknown; error?: { code?: unknown } } | null | undefined;
  const status = typeof err?.status === 'number' ? err.status : null;
  const code = err?.error?.code;
  return { error_code: status === 0 ? 'NETWORK' : typeof code === 'string' && code ? code : 'UNKNOWN', status };
}

/** Megabytes to one decimal */
export function sizeMb(bytes: number | null | undefined): number {
  return Math.round(((bytes ?? 0) / (1024 * 1024)) * 10) / 10;
}

@Injectable({ providedIn: 'root' })
export class DrawingsAnalytics {
  private readonly analytics = inject(AnalyticsService);

  /** Sends the event with only its allowed properties, and only plain values (no objects, no free text by accident) */
  track(event: DrawingsEvent, props: Record<string, unknown> = {}): void {
    const def = DRAWINGS_EVENTS[event];
    const allowed: Record<string, PropValue> = {};
    for (const key of def.props as readonly string[]) {
      const value = props[key];
      if (value === undefined) {
        continue;
      }
      if (value === null || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
        allowed[key] = value as PropValue;
      } else if (Array.isArray(value) && value.every(v => typeof v === 'string')) {
        allowed[key] = value as string[];
      }
    }
    this.analytics.capture(def.name, allowed);
  }

  /** A drawings error the user was shown, where nothing more specific covers it */
  error(surface: string, e: unknown): void {
    this.track('error_shown', { surface, ...errorProps(e) });
  }
}
